//End user new leave list allleaves
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import EmployeeNavbar from "../Layout/EmployeeNavbar";
import API_BASE_URL from "../config";

const MyLeaves = () => {
  const [leaves, setLeaves] = useState([]);
  const [filteredLeaves, setFilteredLeaves] = useState([]);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState("ALL");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [editingLeave, setEditingLeave] = useState(null);
  const [editForm, setEditForm] = useState({
    leaveType: "",
    startDate: "",
    endDate: "",
    reason: "",
    document: null,
  });
  const [cancelModal, setCancelModal] = useState(null); // holds the leave to cancel
  const [cancelReason, setCancelReason] = useState(""); // input value for reason
  const [financialYear, setFinancialYear] = useState("");
  const [leaveSummary, setLeaveSummary] = useState([]);


  const [leaveTypes, setLeaveTypes] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" }); // 🟢 Added
  const navigate = useNavigate();

  // 🟢 Sorting Logic
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedLeaves = [...filteredLeaves].sort((a, b) => {
    if (!sortConfig.key) return 0;
    let aVal = a[sortConfig.key];
    let bVal = b[sortConfig.key];

    if (sortConfig.key === "startDate") {
      aVal = new Date(a.startDate);
      bVal = new Date(b.startDate);
    }

    if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  // 🟢 Row background color by status
  const getRowColor = (status) => {
    switch (status) {
      case "APPROVED":
        return { backgroundColor: "#d4edda" }; // light green
      case "REJECTED":
        return { backgroundColor: "#f8d7da" }; // light red
      case "PENDING":
        return { backgroundColor: "#fff3cd" }; // light yellow
      case "CANCELLED":
        return { backgroundColor: "#e2e3e5" }; // light grey
      default:
        return {};
    }
  };

  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const roles = JSON.parse(sessionStorage.getItem("roles") || "[]");

    if (!token) {
      toast.error("You must be logged in to access this page.");
      navigate("/");
      return;
    }

    // ✅ check admin role
    if (!roles.includes("ROLE_USER")) {
      toast.error("You are not authorized to access this page.");
      navigate("/");
      return;
    }
  }, [navigate]);


  useEffect(() => {
    const token = sessionStorage.getItem("token");

    fetch(`${API_BASE_URL}/api/company-info`, {
      headers: { Authorization: "Bearer " + token },
    })
      .then(res => res.json())
      .then(data => {


        // API returns array → take first record
        const company = Array.isArray(data) ? data[0] : data;

        if (!company?.financialYear) {
          console.warn("No financialYear found");
          return;
        }

        setFinancialYear(company.financialYear);
      })

      .catch(err => console.error("Error fetching company info:", err));
  }, []);


  const fetchLeaveSummary = async () => {
    const token = sessionStorage.getItem("token");
    const employeeId = sessionStorage.getItem("employeeId");

    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/leave-transactions/summary/${employeeId}`,
        {
          params: { financialYear },
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setLeaveSummary(res.data);
    } catch (err) {
      console.error("Error fetching leave summary", err);
    }
  };


  useEffect(() => {
    if (financialYear) {
      fetchLeaves();
      fetchLeaveSummary();
    }
  }, [financialYear]);


  // Fetch leave types
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    if (!token) {
      navigate("/");
      return;
    }

    axios
      .get(`${API_BASE_URL}/api/leave-types/all`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setLeaveTypes(res.data))
      .catch((err) => {
        console.error("Error fetching leave types:", err);
        if ([401, 403].includes(err.response?.status)) {
          sessionStorage.removeItem("token");
          navigate("/");
        }
      });
  }, [navigate]);


  // Fetch user's leaves
  const fetchLeaves = async () => {
    const token = sessionStorage.getItem("token");
    const employeeId = sessionStorage.getItem("employeeId");

    if (!token || !employeeId || !financialYear) {
      return; // ⛔ wait until financialYear is ready
    }

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/leave-transactions/employee/${employeeId}/avail`,
        {
          params: { financialYear }, // ✅ from state
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      const mappedLeaves = response.data.map(l => ({
        ...l,
        status: l.leaveStatus // 🔥 UI expects `status`
      }));

      const sortedLeaves = mappedLeaves.sort(
        (a, b) => new Date(b.startDate) - new Date(a.startDate)
      );

      setLeaves(sortedLeaves);
      setFilteredLeaves(sortedLeaves);

    } catch (error) {
      console.error("Error fetching leaves:", error);
      setError(
        error.response?.status === 403
          ? "You are not authorized to view this data."
          : "Unable to fetch leave data."
      );
    }
  };




  const normalizeLeaveType = (str = "") =>
    str
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/[()]/g, "");



  useEffect(() => {
    if (financialYear) {
      fetchLeaves();
    }
  }, [financialYear]);

  // Filters
  useEffect(() => {
    let data = [...leaves];

    // ✅ DATE VALIDATION: toDate must be >= fromDate
    if (fromDate && toDate && new Date(toDate) < new Date(fromDate)) {
      toast.error("End date must be greater than or equal to start date.");
      return; // ❌ stop filtering
    }

    if (filterType !== "ALL") {
      data = data.filter(
        (l) =>
          normalizeLeaveType(l.leaveType) ===
          normalizeLeaveType(filterType)
      );
    }

    if (filterStatus !== "ALL") data = data.filter((l) => l.status === filterStatus);

    if (fromDate) data = data.filter((l) => new Date(l.startDate) >= new Date(fromDate));
    if (toDate) data = data.filter((l) => new Date(l.endDate) <= new Date(toDate));

    setFilteredLeaves(data);
  }, [filterType, filterStatus, fromDate, toDate, leaves]);


  const clearFilters = () => {
    setFilterType("ALL");
    setFilterStatus("ALL");
    setFromDate("");
    setToDate("");
    setFilteredLeaves(leaves);
  };

  // Edit Modal Handlers
  const openEditModal = (leave) => {
    setEditingLeave(leave);
    setEditForm({
      leaveType: leave.leaveType,
      startDate: leave.startDate,
      endDate: leave.endDate,
      reason: leave.reason,
      document: null,
    });
  };

  const handleEditChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "document") {
      setEditForm({ ...editForm, document: files[0] });
    } else {
      setEditForm({ ...editForm, [name]: value });
    }
  };

  const handleUpdate = async () => {
    const token = sessionStorage.getItem("token");
    if (!token || !editingLeave) return;

    try {
      const requiredDays = calculateDays(
        editForm.startDate,
        editForm.endDate
      );

      let finalLeaveType = editForm.leaveType;

      // find summary for selected leave type
      const summary = leaveSummary.find(
        (s) => s.leaveType === editForm.leaveType
      );

      const availableBalance = summary ? summary.remaining : 0;

      // ❌ Not credited OR insufficient balance → auto LOP
      if (!summary || availableBalance < requiredDays) {
        finalLeaveType = "Loss of Pay(LOP)";
        toast.info("Insufficient balance. Leave changed to Loss of Pay(LOP)");
      }

      await axios.put(
        `${API_BASE_URL}/api/leave-transactions/apply/${editingLeave.id}`,
        {
          leaveType: finalLeaveType,
          startDate: editForm.startDate,
          endDate: editForm.endDate,
          reason: editForm.reason,
          financialYear: editingLeave.financialYear,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setEditingLeave(null);
      fetchLeaves();
      fetchLeaveSummary();
      toast.success("Leave updated successfully!");

    } catch (error) {
      console.error("Update error:", error);
      toast.error(error.response?.data || "Failed to update leave");
    }
  };



  const calculateDays = (start, end) => {
    if (!start || !end) return 0;
    const s = new Date(start);
    const e = new Date(end);
    return Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
  };


  const handleCancel = async () => {
    if (!cancelModal) return;
    if (!cancelReason.trim()) {
      alert("Please enter a cancellation reason.");
      return;
    }

    const token = sessionStorage.getItem("token");
    try {
      await axios.put(
        `${API_BASE_URL}/api/leave-transactions/cancel/${cancelModal.id}`,
        { actionReason: cancelReason },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setCancelModal(null);
      fetchLeaves();
      alert("Leave cancelled successfully!");
    } catch (error) {
      console.error("Cancel error:", error);
      alert("Failed to cancel leave!");
    }
  };


  return (
    <>
      <div className="fixed-top">
        <EmployeeNavbar />
      </div>
      <div className="container-fluid " style={{ paddingTop: "130px" }}>
        {/* Button on near */}
        {/* <div className="d-flex align-items-center mb-4">
          <h2 className="text-start mb-0 me-2">My Leave Requests</h2>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => navigate("/leave")}
          >
            New
          </button>
        </div> */}

        {/* button on last */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 className="text-start mb-0">My Leave Requests</h2>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => navigate("/leave")}
          >
            New
          </button>
        </div>

        {error && <p className="text-danger text-center">{error}</p>}

        {/* Filters */}
        <div className="row mb-3">
          <div className="col-md-2 mb-2">
            <select
              className="form-select"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="ALL">All Leave Types</option>
              {leaveTypes.map((type) => (
                <option key={type.id} value={type.name}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-2 mb-2">
            <select
              className="form-select"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
            >
              <option value="ALL">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
              <option value="FORWARDED">Forwarded</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>

          <div className="col-md-2 mb-2">
            <input
              type="date"
              className="form-control"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>

          <div className="col-md-2 mb-2">
            <input
              type="date"
              className="form-control"
              value={toDate}
              min={fromDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>

          {/* <div className="col-md-1 mb-2 d-grid">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={clearFilters}
            >
              Clear Filters
            </button>
          </div> */}
        </div>

        {/* Table */}
        <div className="table-responsive" style={{ height: "75vh", overflowY: "auto", overflowX: "auto" }}>
          <table className="table align-middle text-start">
            <thead className="position-sticky top-0">
              <tr>
                <th style={{ backgroundColor: "#DFE4EB" }}>Sr</th>
                <th style={{ backgroundColor: "#DFE4EB" }}>Edit | Cancel</th>

                {/*  Sortable Columns */}
                <th
                  style={{ backgroundColor: "#DFE4EB", cursor: "pointer" }}
                  onClick={() => handleSort("leaveType")}
                >
                  Leave Type{" "}
                  {sortConfig.key === "leaveType"
                    ? sortConfig.direction === "asc"
                      ? "↑" : "↓"
                    : ""}
                </th>

                <th
                  style={{ backgroundColor: "#DFE4EB", cursor: "pointer" }}
                  onClick={() => handleSort("startDate")}
                >
                  Start Date{" "}
                  {sortConfig.key === "startDate"
                    ? sortConfig.direction === "asc"
                      ? "↑" : "↓"
                    : ""}
                </th>

                <th
                  style={{ backgroundColor: "#DFE4EB", cursor: "pointer" }}
                  onClick={() => handleSort("endDate")}
                >
                  End Date{" "}
                  {sortConfig.key === "endDate"
                    ? sortConfig.direction === "asc"
                      ? "↑" : "↓"
                    : ""}
                </th>

                <th style={{ backgroundColor: "#DFE4EB" }}>
                  Total Days
                </th>


                <th style={{ backgroundColor: "#DFE4EB" }}>Leave Reason</th>
                {/* <th style={{ backgroundColor: "#DFE4EB" }}>Document</th> */}

                <th
                  style={{ backgroundColor: "#DFE4EB", cursor: "pointer" }}
                  onClick={() => handleSort("status")}
                >
                  Status{" "}
                  {sortConfig.key === "status"
                    ? sortConfig.direction === "asc"
                      ? "↑" : "↓"
                    : ""}
                </th>

                <th style={{ backgroundColor: "#DFE4EB" }}>Remark</th>
              </tr>
            </thead>
            <tbody>
              {sortedLeaves.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center text-muted">
                    No leave records found.
                  </td>
                </tr>
              ) : (
                sortedLeaves.map((leave, index) => (
                  <tr key={leave.id} style={getRowColor(leave.status)}>
                    <td className="text-start align-middle">{index + 1}</td>
                    <td className="text-start align-middle">
                      {leave.status === "PENDING" && (
                        <>
                          <span
                            style={{
                              cursor: "pointer",
                              color: "#007bff",
                              fontSize: "0.875rem",
                              fontWeight: "500",
                              marginRight: "0.5rem",
                            }}
                            onClick={() => openEditModal(leave)}
                          >
                            Edit
                          </span>
                          {new Date(leave.startDate) > new Date() ? (
                            <span
                              style={{
                                cursor: "pointer",
                                color: "#6c757d",
                                fontSize: "0.875rem",
                                fontWeight: "500",
                                marginRight: "0.5rem",
                              }}
                              onClick={() => {
                                setCancelModal(leave);
                                setCancelReason("");
                              }}

                            >
                              Cancel
                            </span>
                          ) : (
                            <span
                              style={{
                                cursor: "not-allowed",
                                color: "#6c757d",
                                fontSize: "0.875rem",
                                fontWeight: "500",
                                marginRight: "0.5rem",
                                opacity: 0.5,
                              }}
                            >
                              Cancel
                            </span>
                          )}
                        </>
                      )}
                    </td>

                    <td className="text-start align-middle">
                      {leave.leaveType}
                    </td>

                    {/* Start Date */}
                    <td className="text-start align-middle">
                      {new Date(leave.startDate).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>

                    {/* End Date */}
                    <td className="text-start align-middle">
                      {new Date(leave.endDate).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </td>

                    {/* Total Days */}
                    <td className="text-start align-middle">
                      {(() => {
                        const start = new Date(leave.startDate);
                        const end = new Date(leave.endDate);
                        const diff = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
                        return diff > 0 ? diff : 0;
                      })()}
                    </td>


                    <td className="text-start align-middle">{leave.reason}</td>

                    {/* <td className="text-start align-middle">
                      {leave.supportiveDocument ? (
                        <a
                          href={`${API_BASE_URL}/uploads/${leave.supportiveDocument}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {leave.supportiveDocument}
                        </a>
                      ) : (
                        ""
                      )}
                    </td> */}

                    <td className="fw-normal text-start">{leave.status}</td>

                    <td className="text-start align-middle">{leave.rejectionReason || leave.cancelReason || leave.actionReason || ""}</td>

                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Modal */}
      {editingLeave && (
        <div
          className="modal show fade d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Leave Request</h5>
                <button
                  className="btn-close"
                  onClick={() => setEditingLeave(null)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Leave Type</label>
                  <select
                    name="leaveType"
                    value={editForm.leaveType}
                    onChange={handleEditChange}
                    className="form-select"
                  >
                    <option>Select</option>
                    {leaveTypes.map((type) => (
                      <option key={type.id} value={type.name}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    value={editForm.startDate}
                    onChange={handleEditChange}
                    className="form-control"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">End Date</label>
                  <input
                    type="date"
                    name="endDate"
                    value={editForm.endDate}
                    onChange={handleEditChange}
                    className="form-control"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Reason</label>
                  <textarea
                    name="reason"
                    value={editForm.reason}
                    onChange={handleEditChange}
                    className="form-control"
                  />
                </div>

                {editForm.leaveType === "SICK" && (
                  <div className="mb-3">
                    <label className="form-label">Supportive Document</label>
                    <input
                      type="file"
                      name="document"
                      onChange={handleEditChange}
                      className="form-control"
                    />
                    {editingLeave.supportiveDocument && (
                      <div>
                        <small className="text-muted">Current: </small>
                        <a
                          href={`${API_BASE_URL}/uploads/${editingLeave.supportiveDocument}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {editingLeave.supportiveDocument}
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setEditingLeave(null)}
                >
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={handleUpdate}>
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Reason Modal */}
      {cancelModal && (
        <div
          className="modal show fade d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: "#607799", color: "rgba(253, 252, 252, 1)" }}>
                <h5 className="modal-title">Cancel Leave Request</h5>
                <button
                  className="btn-close"
                  onClick={() => setCancelModal(null)}
                ></button>
              </div>
              <div className="modal-body">
                <p>
                  <strong>
                    Are you sure you want to cancel this{" "}
                    {cancelModal.leaveType} leave?
                  </strong>
                </p>
                <div className="mb-3">
                  <label className="form-label">Please Provide the Reason for Cancellation</label>
                  <textarea
                    className="form-control"
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    rows="3"
                    placeholder="Enter your reason here..."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setCancelModal(null)}
                >
                  Close
                </button>
                <button className="btn btn-primary" onClick={handleCancel}>
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


    </>
  );
};

export default MyLeaves;
